"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";

import { SidebarContent } from "@/components/layout/sidebar/sidebar-layout";
import { SidebarMainContent } from "@/components/layout/sidebar/main-content";
import { SettingsPanel } from "@/components/layout/sidebar/mobile/settings-panel";
import { SequencePanel } from "@/components/layout/sidebar/mobile/sequence-panel";
import { SearchTabsPanel } from "@/components/layout/sidebar/mobile/search-tabs-panel";
import { JobPanel } from "@/components/layout/sidebar/mobile/job-panel";
import { ConversationsPanel } from "@/components/layout/sidebar/mobile/conversations-panel";
import { matchJobRoute } from "@/features/jobs/components/view/layout/job-tab-items";
import { matchSequenceRoute } from "@/features/sequences/components/detail/sequence-tab-items";
import { matchSearchRoute } from "@/components/search/search-tabs";
import { useConversationsFeature } from "@/features/conversations/hooks/use-conversations-feature";

const panelSpring = {
  type: "spring",
  damping: 30,
  stiffness: 400,
  mass: 0.5,
} as const;

type Panel = "main" | "settings" | "sequence" | "search" | "job" | "conversations";

/**
 * Contextual initial panel: opening the drawer while on a job / sequence /
 * settings / search route lands directly on the deepest relevant submenu
 * (Back then walks up the hierarchy). A sequence-detail route is also a job
 * route (`matchJobRoute` matches it), so it's checked first.
 */
export function resolveInitialPanel(
  pathname: string,
  conversationsEnabled: boolean,
): Panel {
  if (matchSequenceRoute(pathname)) return "sequence";
  if (matchJobRoute(pathname)) return "job";
  if (
    conversationsEnabled &&
    (pathname === "/conversations" || pathname.startsWith("/conversations/"))
  ) {
    return "conversations";
  }
  if (pathname.startsWith("/settings")) return "settings";
  if (matchSearchRoute(pathname)) return "search";
  return "main";
}

export function MobileSidebarPanels() {
  const pathname = usePathname();
  const conversationsEnabled = useConversationsFeature();
  // Mounted fresh on every drawer open (the shell unmounts children when
  // closed), so this initializer re-runs on each open and the initial panel
  // always reflects the current route.
  const [panel, setPanel] = useState<Panel>(() =>
    resolveInitialPanel(pathname, conversationsEnabled),
  );

  useEffect(() => {
    if (!conversationsEnabled || panel !== "main") return;
    if (resolveInitialPanel(pathname, true) === "conversations") {
      setPanel("conversations");
    }
  }, [conversationsEnabled, panel, pathname]);

  return (
    <div className="relative min-h-0 flex-1 overflow-hidden">
      <AnimatePresence initial={false}>
        {panel === "main" ? (
          <motion.div
            key="main"
            className="absolute inset-0 flex flex-col"
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={panelSpring}
          >
            <SidebarContent>
              <SidebarMainContent
                onSettingsDrillIn={() => setPanel("settings")}
                onJobDrillIn={() => setPanel("job")}
                onSearchDrillIn={() => setPanel("search")}
                onConversationsDrillIn={
                  conversationsEnabled
                    ? () => setPanel("conversations")
                    : undefined
                }
              />
            </SidebarContent>
          </motion.div>
        ) : panel === "settings" ? (
          <motion.div
            key="settings"
            className="absolute inset-0 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={panelSpring}
          >
            <SidebarContent>
              <SettingsPanel onBack={() => setPanel("main")} />
            </SidebarContent>
          </motion.div>
        ) : panel === "sequence" ? (
          <motion.div
            key="sequence"
            className="absolute inset-0 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={panelSpring}
          >
            <SidebarContent>
              <SequencePanel onBack={() => setPanel("job")} />
            </SidebarContent>
          </motion.div>
        ) : panel === "job" ? (
          <motion.div
            key="job"
            className="absolute inset-0 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={panelSpring}
          >
            <SidebarContent>
              <JobPanel
                onBack={() => setPanel("main")}
                onSequenceDrillIn={() => setPanel("sequence")}
              />
            </SidebarContent>
          </motion.div>
        ) : panel === "conversations" ? (
          <motion.div
            key="conversations"
            className="absolute inset-0 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={panelSpring}
          >
            <SidebarContent>
              <ConversationsPanel onBack={() => setPanel("main")} />
            </SidebarContent>
          </motion.div>
        ) : (
          <motion.div
            key="search"
            className="absolute inset-0 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={panelSpring}
          >
            <SidebarContent>
              <SearchTabsPanel onBack={() => setPanel("main")} />
            </SidebarContent>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
